import { useTranslation } from "react-i18next";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as ArrowIcon } from "assets/svgs/Arrow.svg";
import { ReactComponent as ImageIcon } from "assets/svgs/Image.svg";
import { ReactComponent as StairIcon } from "assets/svgs/Stair.svg";
import { ReactComponent as ClockIcon } from "assets/svgs/Clock.svg";
import { ReactComponent as DockingIcon } from "assets/svgs/docking.svg";
import { ReactComponent as LocationIcon } from "assets/svgs/navigation/Location.svg";
import { ReactComponent as WarningIcon } from "assets/svgs/toast/Warning.svg";
import { ReactComponent as ControlArmIcon } from "assets/svgs/navbar/robot-arm-svgrepo-com.svg";
import { useDeveloperMode } from "providers/DeveloperModeProvider";
import { useFeatures } from "providers/FeaturesProvider";

interface ActionButtonsProps {
  onCreateWaypoint: () => void;
  onNavigateStair: () => void;
  onDocking: () => void;
  onTakePhoto: () => void;
  onRecord360: () => void;
  onWait: () => void;
  onLoadMap: () => void;
  onSaveMap: () => void;
  onRelocalization: () => void;
  onArm: () => void;
  onFailure: () => void;
  actionsDisabled?: boolean;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onCreateWaypoint,
  onNavigateStair,
  onDocking,
  onTakePhoto,
  onRecord360,
  onWait,
  onLoadMap,
  onSaveMap,
  onRelocalization,
  onArm,
  onFailure,
  actionsDisabled = false,
}) => {
  const { t } = useTranslation("missions");
  const { developerModeEnabled } = useDeveloperMode();
  const { hasArm } = useFeatures();
  const enabledClass =
    "flex h-8 min-h-8 items-center justify-center space-x-1.5 rounded-r5 bg-black70 px-2.5 hover:bg-black60";
  const disabledClass =
    "flex h-8 min-h-8 cursor-not-allowed items-center justify-center space-x-1.5 rounded-r5 bg-black80 px-2.5 text-black50";
  const titleClass = "!normal-case !text-h5 !leading-none !font-normal whitespace-nowrap";

  return (
    <div className="grid w-full grid-cols-3 gap-2">
      <FunctionalButton
        icon={<ArrowIcon className="h-4 w-4" />}
        title={t("actions.createWaypoint")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onCreateWaypoint}
      />
      <FunctionalButton
        icon={<StairIcon className="h-4 w-4" />}
        title={t("actions.navigateStair")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onNavigateStair}
      />
      <FunctionalButton
        icon={<DockingIcon className="h-4 w-4" />}
        title={t("actions.docking")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onDocking}
      />
      <FunctionalButton
        icon={<ImageIcon className="h-4 w-4" />}
        title={t("actions.takePhoto")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onTakePhoto}
      />
      <FunctionalButton
        icon={<ImageIcon className="h-4 w-4" />}
        title={t("actions.record360")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onRecord360}
      />
      <FunctionalButton
        icon={<ClockIcon className="h-4 w-4" />}
        title={t("actions.wait")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onWait}
      />
      <FunctionalButton
        icon={<ArrowIcon className="h-4 w-4 rotate-180" />}
        title={t("actions.loadMap")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onLoadMap}
      />
      <FunctionalButton
        icon={<ArrowIcon className="h-4 w-4 -rotate-90" />}
        title={t("actions.saveMap")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onSaveMap}
      />
      <FunctionalButton
        icon={<LocationIcon className="h-4 w-4" />}
        title={t("actions.relocalization")}
        titleClassName={titleClass}
        className={actionsDisabled ? disabledClass : enabledClass}
        disabled={actionsDisabled}
        onClick={onRelocalization}
      />
      {hasArm && (
        <FunctionalButton
          icon={<ControlArmIcon className="h-4 w-4" />}
          title={t("actions.arm")}
          titleClassName={titleClass}
          className={actionsDisabled ? disabledClass : enabledClass}
          disabled={actionsDisabled}
          onClick={onArm}
        />
      )}
      {developerModeEnabled && (
        <FunctionalButton
          icon={<WarningIcon className="h-4 w-4 text-warning" />}
          title={t("actions.failure")}
          titleClassName={titleClass}
          className={actionsDisabled ? disabledClass : enabledClass}
          disabled={actionsDisabled}
          onClick={onFailure}
        />
      )}
    </div>
  );
};

export default ActionButtons;
